import React, { useEffect, useState } from 'react';
import { Droppable, DroppableProps } from '@hello-pangea/dnd';

/**
 * StrictModeDroppable Component
 * 
 * Wrapper for @hello-pangea/dnd Droppable that works correctly with React 18 StrictMode.
 * 
 * **Problem**: React 18 StrictMode intentionally double-renders components in development
 * to help detect side effects. This causes issues with Droppable's internal DOM refs.
 * 
 * **Solution**: Delay Droppable mount by one animation frame using requestAnimationFrame.
 * This ensures the component is fully mounted before Droppable initializes.
 * 
 * **Industry Pattern**: Recommended by @hello-pangea/dnd community for StrictMode compatibility.
 * 
 * @example
 * ```tsx
 * <StrictModeDroppable droppableId="list">
 *   {(provided) => (
 *     <div ref={provided.innerRef} {...provided.droppableProps}>
 *       {items.map((item, index) => (
 *         <Draggable key={item.id} draggableId={item.id} index={index}>
 *           ...
 *         </Draggable>
 *       ))}
 *       {provided.placeholder}
 *     </div>
 *   )}
 * </StrictModeDroppable>
 * ```
 */
export const StrictModeDroppable = ({ children, ...props }: DroppableProps) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // Use requestAnimationFrame to defer rendering until after React's render cycle
    const animation = requestAnimationFrame(() => setEnabled(true));
    
    return () => {
      // Clean up on unmount
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  // Don't render Droppable until after first animation frame
  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};

StrictModeDroppable.displayName = 'StrictModeDroppable';

